import { useState } from 'react';
import { Grid } from '@ui-propulsion/react-components';
import { Palette, Tag } from '@diamond-design/components';
import { CheckVerified02 } from '@diamond-design/icons';
import parse from 'html-react-parser';
import PropTypes from 'prop-types';
import CtaButton from '../ctaButton/ctaButton';
import ModalPopup from '../modalPopup/modalPopup';
import styles from './card.module.scss';
import RatesTableList from '../ratesTable/rateTableList/ratesTableList';
import handleCTAClick from '../../services/helpers/handleCTAClick';

function icon(props) {
  return (
    <CheckVerified02
      {...props}
      color={Palette.success[700]}
      width="12"
      height="12"
    />
  );
}
export default function Card({
  headline = '',
  description = '',
  tagText,
  italicDescription,
  image = null,
  tooltip = null,
  cta = null,
  ratesTable = null,
  theme = 'grey',
  className = '',
  cardContainerClassName = '',
  headlineClassName = '',
  descriptionClassName = '',
  ctaButtonClassName = '',
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const imagePosition = image?.position ? image.position : 'Top';
  const imageContent = image ? (
    <img
      src={image.path}
      alt={image.alt}
      {...{
        ...(image.width && { width: image.width }),
        ...(image.height && { height: image.height }),
      }}
      className={styles.image}
    />
  ) : null;
  const cardContent = (
    <div className={`${styles.container} ${cardContainerClassName}`}>
      {tagText && (
        <div className={styles.tagContainer}>
          <Tag
            label={tagText}
            type="success"
            iconLeft={(props) => icon(props)}
          />
        </div>
      )}
      {headline && (
        <h3 className={`${styles.headline} ${headlineClassName}`}>
          {parse(headline)}
        </h3>
      )}
      {description && (
        <div className={`${styles.description} ${descriptionClassName}`}>
          {parse(description)}
        </div>
      )}
      {italicDescription && (
        <i className={styles.italicDescription}>{italicDescription}</i>
      )}
      {tooltip && (
        <>
          <p
            role="presentation"
            className={styles.tooltip}
            onClick={() => setIsModalOpen(true)}
          >
            {tooltip.label}
          </p>
          {tooltip.content && (
            <ModalPopup
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
            >
              <div className={styles.popupContent}>
                {parse(tooltip.content)}
              </div>
            </ModalPopup>
          )}
        </>
      )}
      {cta && (
        <div
          className={`${styles.applyCta} ${
            theme === 'darkBlue' && cta.variant === 'secondary'
              ? styles.bgWhite
              : ''
          } ${ctaButtonClassName}`}
        >
          <CtaButton
            label={cta?.label}
            href={cta?.link}
            color={cta.color}
            variant={cta.variant}
            data-testid="cta-btn"
            onClick={(e) => handleCTAClick(e, { link: cta?.link })}
          />
        </div>
      )}
      {ratesTable && <RatesTableList {...ratesTable} type="card" />}
    </div>
  );
  return (
    <div className={`${styles.card} ${className} ${styles[theme]}`}>
      {imagePosition === 'Top' ? (
        <div className={styles.topImageContainer}>
          {imageContent}
          {cardContent}
        </div>
      ) : (
        <Grid container>
          {imagePosition === 'Left' ? (
            <>
              <Grid item xs={4}>
                {imageContent}
              </Grid>
              <Grid item xs={8}>
                {cardContent}
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={8}>
                {cardContent}
              </Grid>
              <Grid item xs={4}>
                {imageContent}
              </Grid>
            </>
          )}
        </Grid>
      )}
    </div>
  );
}

Card.propTypes = {
  headline: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.shape({
    path: PropTypes.string,
    alt: PropTypes.string,
    position: PropTypes.oneOf([null, 'Top', 'Left', 'Right']),
  }),
  tooltip: PropTypes.shape({
    label: PropTypes.string,
    content: PropTypes.string,
  }),
  cta: PropTypes.shape({
    label: PropTypes.string,
    link: PropTypes.string,
    variant: PropTypes.oneOf([null, 'primary', 'secondary']),
  }),
  ratesTable: PropTypes.shape({
    data: PropTypes.array,
    ctaLabel: PropTypes.string,
  }),
  theme: PropTypes.oneOf(['grey', 'darkBlue']),
};
